import { FC, FunctionComponent, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import SignUp from '../pages/auth/SignUp';
import ProjectBreadcrumbs from '../components/Breadcrumbs/ProjectBreadcrumbs';
import TeamDetailsBreadcrumbs from '../components/Breadcrumbs/TeamDetailsBreadcrumbs';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthStates, useAuth } from '../contexts/authContext';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router';
import { getInviteId } from '../hooks/api/auth';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import CriteriaBreadcrumbs from 'components/Breadcrumbs/CriteriaBreadCrumbs';
import CvProjectBreadCrumbs from 'components/Breadcrumbs/CvProjectBreadCrumbs';
import CompanyBreadcrumbs from 'components/Breadcrumbs/CompanyBreadCrumbs';
import CreateSalesNavigatorLink from 'pages/CreateSalesNavigatorLink';

const Login = lazy(() => import('pages/auth/Login'));
const ApiDocumentation = lazy(() => import('pages/ApiDocumentation'));
const Home = lazy(() => import('pages/home'));
const Projects = lazy(() => import('pages/Projects'));
const ProjectDetails = lazy(() => import('pages/ProjectDetails'));
const CvProjectDetails = lazy(() => import('pages/CvProjectDetails'));
const Settings = lazy(() => import('pages/Settings'));
const SenderSettings = lazy(() => import('pages/SenderSettings'));
const TeamsSettings = lazy(() => import('pages/TeamsSettings'));
const ManageApiKeys = lazy(() => import('pages/ManageApiKeys'));
const WebhookIntegrations = lazy(() => import('pages/WebhookIntegrations'));
const ConnectionNotes = lazy(() => import('pages/ConnectionNotes'));
const TeamsSettingsDetails = lazy(() => import('pages/TeamDetailSettings'));
const HubDashboard = lazy(() => import('pages/HubsDashboard'));
const CreateHubFilters = lazy(() => import('pages/CreateHubFilters'));
const CreateHubCvs = lazy(() => import('pages/CreateHubCvs'));
const ReviewHubCvs = lazy(() => import('pages/ReviewHubCvs'));
const ReviewHub = lazy(() => import('pages/ReviewHub'));
const Criterias = lazy(() => import('pages/Criterias'));
const Companies = lazy(() => import('pages/Companies'));
const VpsManager = lazy(() => import('pages/VpsManager'));
const CompanyDetails = lazy(() => import('pages/CompanyDetails'));
const CreateCriteria = lazy(() => import('pages/CreateCriteria'));
const CriteriaDetails = lazy(() => import('pages/CriteriaDetails'));
const PersonalSettings = lazy(() => import('pages/PersonalSettings'));
const Billing = lazy(() => import('pages/Billing'));
const BillingVerifone = lazy(() => import('pages/BillingVerifone'));
const RefreshCenter = lazy(() => import('pages/RefreshCenter'));
const MainLayout = lazy(() => import('../layouts/MainLayout'));

type routeItem = {
  path: string;
  key: string;
  exact: boolean;
  breadcrumb?: FunctionComponent<any> | string;
  component: Function;
};

type routes = routeItem & {
  routes?: routeItem[];
};

const AuthLayout: FC<JSX.IntrinsicAttributes & { routes: routes[] }> = (props) => {
  const { state, token } = useAuth();

  const { search } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const teamId = params.get('id');
    if (teamId && /^[\dA-Fa-f]{24}$/.test(teamId)) {
      localStorage.setItem('invitationId', teamId);
    }
  }, []);

  if (!token) {
    if (state === AuthStates.NotLogged && getInviteId()) {
      toast.success('You need to sign in first to accept the invitation!', { toastId: 'session-expired' });
    }
    if (state === AuthStates.NotLogged) {
      toast.info('Please sign in to continue.', { toastId: 'session-expired' });
    }
    if (state === AuthStates.LoggedOut) {
      toast.success('Logged out successfully, see you soon!', { toastId: 'session-expired' });
    }
    if (state === AuthStates.TokenExpired) {
      toast.error('Session expired. Please login again.', { toastId: 'session-expired' });
    }

    return <Redirect to={'/'} />;
  }

  const useInvitationToTeam = (
    <>
      <Redirect to={`app/settings/teams?id=${getInviteId()}`} />
    </>
  );

  return (
    <MainLayout>
      <Suspense
        fallback={
          <Grid alignItems="center" container direction="column" justifyContent="center" sx={{ height: '100%' }}>
            <CircularProgress />
          </Grid>
        }>
        <RenderRoutes {...props} />
        {getInviteId() && useInvitationToTeam}
      </Suspense>
    </MainLayout>
  );
};

const RootRedirector: FC = () => {
  const { token } = useAuth();

  if (!token) {
    return <Redirect to={'/login'} />;
  }
  return <Redirect to={'/app'} />;
};

const ROUTES: routes[] = [
  {
    path: '/',
    key: 'ROOT',
    exact: true,
    component: RootRedirector,
    routes: []
  },
  {
    path: '/login',
    key: 'LOGIN',
    exact: true,
    component: Login,
    routes: []
  },
  {
    path: '/sign-up',
    key: 'SIGNUP',
    exact: true,
    component: SignUp,
    routes: []
  },
  {
    path: '/forgot-password',
    key: 'FORGOT_PASS',
    exact: true,
    component: ForgotPassword,
    routes: []
  },
  {
    path: '/reset-password',
    key: 'RESET_PASS',
    exact: true,
    component: ResetPassword,
    routes: []
  },
  {
    path: '/documentation',
    key: 'API_DOCUMENTATION',
    exact: true,
    component: ApiDocumentation,
    routes: []
  },
  {
    path: '/app',
    exact: false,
    key: 'APP',
    component: AuthLayout,
    routes: [
      {
        path: '/app',
        key: 'APP_ROOT',
        exact: true,
        component: Home
      },
      {
        path: '/app/generate-salesnavigator-link',
        key: 'CREATE_SALESNAVIGATOR_LINK',
        exact: true,
        component: CreateSalesNavigatorLink 
      },
      {
        path: '/app/projects',
        key: 'PROJECTS',
        exact: true,
        breadcrumb: 'Hubs',
        component: Projects
      },
      {
        path: '/app/projects/:projectId',
        key: 'PROJECT_DETAILS',
        exact: false,
        breadcrumb: ProjectBreadcrumbs,
        component: ProjectDetails
      },
      {
        path: '/app/create-hub-filters',
        key: 'CREATE_HUB_FILTERS',
        breadcrumb: 'Create Hub from Filters',
        exact: true,
        component: CreateHubFilters
      },
      {
        path: '/app/create-hub-cvs',
        key: 'CREATE_HUB_CVS',
        breadcrumb: 'Create Hub from CVs',
        exact: true,
        component: CreateHubCvs
      },
      {
        path: '/app/review-hub-cvs/:projectId',
        key: 'REVIEW_HUB_CVS',
        breadcrumb: 'Review Hub from CVs',
        exact: true,
        component: ReviewHubCvs
      },
      {
        path: '/app/review-hub/:projectId',
        key: 'REVIEW_HUB',
        breadcrumb: 'Review Hub from Filters',
        exact: true,
        component: ReviewHub
      },
      {
        path: '/app/cv-projects/:projectId',
        key: 'CV_PROJECT_DETAILS',
        breadcrumb: CvProjectBreadCrumbs,
        exact: true,
        component: CvProjectDetails
      },

      /*       {
        path: '/app/dashboard',
        key: 'DASHBOARD',
        breadcrumb: 'Dashboard',
        exact: true,
        component:  HubDashboard
      }, */
      {
        path: '/app/companies',
        key: 'COMPANIES',
        breadcrumb: 'Companies',
        exact: true,
        component: Companies
      },
      {
        path: '/app/vps-manager',
        key: 'VPS_MANAGER',
        breadcrumb: 'VPS',
        exact: true,
        component: VpsManager
      },
      {
        path: '/app/criterias',
        key: 'CRITERIAS',
        breadcrumb: 'Criterias',
        exact: true,
        component: Criterias
      },
      {
        path: '/app/refresh-center',
        key: 'REFRESH_CENTER',
        breadcrumb: 'Refresh Center',
        exact: true,
        component: RefreshCenter
      },
      {
        path: '/app/criterias/create',
        key: 'CREATE_CRITERIA',
        breadcrumb: 'Create Criteria',
        exact: true,
        component: CreateCriteria
      },
      {
        path: '/app/criterias/:criteriaId',
        key: 'CRITERIA_DETAILS',
        exact: true,
        breadcrumb: CriteriaBreadcrumbs,
        component: CriteriaDetails
      },
      {
        path: '/app/companies/:companyId',
        key: 'COMPANIES_DETAILS',
        breadcrumb: CompanyBreadcrumbs,
        exact: true,
        component: CompanyDetails
      },
      {
        path: '/app/admin',
        key: 'ADMIN_PANEL',
        breadcrumb: 'Admin Panel',
        exact: true,
        component: HubDashboard
      },
      {
        path: '/app/settings',
        key: 'SETTINGS',
        breadcrumb: 'Settings',
        exact: true,
        component: Settings
      },
      {
        path: '/app/settings/sender',
        key: 'SENDER_SETTINGS',
        breadcrumb: 'Sender settings',
        exact: true,
        component: SenderSettings
      },
      {
        path: '/app/settings/personal',
        key: 'PERSONAL_SETTINGS',
        breadcrumb: 'Personal settings',
        exact: true,
        component: PersonalSettings
      },
      {
        path: '/app/settings/billing',
        key: 'BILLING',
        breadcrumb: 'Billing',
        exact: true,
        component: BillingVerifone
        // component: Billing
      },
      {
        path: '/app/settings/teams',
        key: 'TEAMS_SETTINGS',
        breadcrumb: 'Teams settings',
        exact: true,
        component: TeamsSettings
      },
      {
        path: '/app/settings/teams/:id',
        key: 'TEAM_SETTINGS_DETAILS',
        exact: true,
        breadcrumb: TeamDetailsBreadcrumbs,
        component: TeamsSettingsDetails
      },
      {
        path: '/app/settings/apis',
        key: 'API_KEYS_SETTINGS',
        breadcrumb: 'Manage API keys',
        exact: true,
        component: ManageApiKeys
      },
      {
        path: '/app/settings/webhooks-integrations',
        key: 'WEBHOOKS_INTEGRATIONS',
        breadcrumb: 'Webhooks & Integrations',
        exact: true,
        component: WebhookIntegrations
      },
      {
        path: '/app/settings/connection-notes',
        key: 'CONNECTION_NOTES',
        breadcrumb: 'Connection Notes',
        exact: true,
        component: ConnectionNotes
      }
    ]
  }
];

export default ROUTES;

export function RenderRoutes({ routes }: { routes: routes[] }) {
  return (
    <Switch>
      {routes.map((route, i) => {
        // eslint-disable-next-line react/jsx-key
        return <RouteWithSubRoutes {...route} />;
      })}

      <Route component={() => <h1>Not Found!</h1>} />
    </Switch>
  );
}

function RouteWithSubRoutes(route: routes) {
  return (
    <Route
      key={route.key}
      exact={route.exact}
      path={route.path}
      render={(props) => <route.component {...props} routes={route.routes} />}
    />
  );
}
